import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { aiPipelineApi } from '@/api/aiPipeline';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Select } from '@/components/ui/Select';
import { LEVELS, SKILLS, formatDateTime, queueParams, type QueueFilters } from '@/lib/aiPipeline';

import { ChecksPill, ItemKinds, ReviewPill, StatePill } from './labels';
import { ApiErrorText } from './notices';

const STATES = ['queued', 'generating', 'checking_quality', 'checking_format', 'done', 'error'] as const;

type Props = {
  filters: QueueFilters;
  onFilters: (f: QueueFilters) => void;
  onOpen: (id: string) => void;
};

/**
 * Очередь элементов (договор §3 `GET /items`). По умолчанию — ждущие решения.
 * Фильтры живут на странице: вернувшись из карточки, проверяющая видит тот же список.
 */
export function QueueTab({ filters, onFilters, onOpen }: Props) {
  const { t, i18n } = useTranslation();
  const q = useInfiniteQuery({
    queryKey: ['ai-pipeline', 'items', filters],
    queryFn: ({ pageParam }) => aiPipelineApi.listItems(queueParams(filters, pageParam)),
    initialPageParam: 0,
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((n, p) => n + (p.items?.length ?? 0), 0);
      return last.items?.length && loaded < (last.total ?? 0) ? loaded : undefined;
    },
  });

  const pages = q.data?.pages ?? [];
  const items = pages.flatMap((p) => p.items ?? []);
  const total = pages.length ? (pages[pages.length - 1].total ?? 0) : 0;
  const set = (key: keyof QueueFilters, value: string) => onFilters({ ...filters, [key]: value });

  return (
    <Card className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Select
          data-ai-filter="review_status"
          label={t('adminAiPipeline.queue.filterReview')}
          value={filters.review_status}
          onChange={(e) => set('review_status', e.target.value)}
          options={[
            { value: 'pending', label: t('adminAiPipeline.review.pending') },
            { value: 'approved', label: t('adminAiPipeline.review.approved') },
            { value: 'rejected', label: t('adminAiPipeline.review.rejected') },
            { value: '', label: t('adminAiPipeline.queue.all') },
          ]}
        />
        <Select
          data-ai-filter="skill"
          label={t('adminAiPipeline.queue.filterSkill')}
          value={filters.skill}
          onChange={(e) => set('skill', e.target.value)}
          options={[
            { value: '', label: t('adminAiPipeline.queue.all') },
            ...SKILLS.map((s) => ({ value: s, label: t(`adminAiPipeline.skill.${s}`) })),
          ]}
        />
        <Select
          data-ai-filter="checks"
          label={t('adminAiPipeline.queue.filterChecks')}
          value={filters.checks}
          onChange={(e) => set('checks', e.target.value)}
          options={[
            { value: '', label: t('adminAiPipeline.queue.any') },
            { value: 'failed', label: t('adminAiPipeline.queue.checksFailed') },
            { value: 'passed', label: t('adminAiPipeline.queue.checksPassed') },
            { value: 'unknown', label: t('adminAiPipeline.queue.checksUnknown') },
          ]}
        />
        <Select
          data-ai-filter="state"
          label={t('adminAiPipeline.queue.filterState')}
          value={filters.state}
          onChange={(e) => set('state', e.target.value)}
          options={[
            { value: '', label: t('adminAiPipeline.queue.any') },
            ...STATES.map((s) => ({ value: s, label: t(`adminAiPipeline.state.${s}`) })),
          ]}
        />
        <Select
          data-ai-filter="level"
          label={t('adminAiPipeline.queue.filterLevel')}
          value={filters.level}
          onChange={(e) => set('level', e.target.value)}
          options={[{ value: '', label: t('adminAiPipeline.queue.any') }, ...LEVELS.map((l) => ({ value: l, label: l }))]}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm text-on-surface-variant">
          {q.isSuccess ? t('adminAiPipeline.queue.shown', { n: items.length, total }) : ''}
        </span>
        <Button variant="ghost" size="sm" onClick={() => void q.refetch()}>
          <span aria-hidden className="material-symbols-outlined text-lg">
            refresh
          </span>
          {t('adminAiPipeline.common.refresh')}
        </Button>
      </div>

      {q.isLoading ? <p className="text-sm text-on-surface-variant">{t('adminAiPipeline.common.loading')}</p> : null}
      {q.isError ? <ApiErrorText error={q.error} /> : null}
      {q.isSuccess && !items.length ? (
        <p className="py-6 text-center text-sm text-on-surface-variant">{t('adminAiPipeline.queue.empty')}</p>
      ) : null}

      {items.length ? (
        <div className="divide-y divide-outline/30">
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              data-ai-row={it.id}
              onClick={() => onOpen(it.id)}
              className="flex w-full flex-wrap items-center gap-x-4 gap-y-2 rounded-xl px-3 py-3 text-left transition hover:bg-surface-container"
            >
              <div className="min-w-0 flex-1 basis-64">
                <div className="font-medium text-on-surface">
                  <span>{t(`adminAiPipeline.skill.${it.skill}`, it.skill)}</span>
                  <span className="text-on-surface-variant"> · </span>
                  <ItemKinds item={it} />
                </div>
                <div className="text-xs text-on-surface-variant">
                  {[formatDateTime(it.created_at, i18n.language), it.cefr_level, t(`adminAiPipeline.trigger.${it.trigger}`, it.trigger)]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <StatePill state={it.pipeline_state} />
                <ChecksPill passed={it.checks_passed} />
                <ReviewPill status={it.review_status} />
                {it.rating ? <Pill>{t('adminAiPipeline.queue.rating', { n: it.rating })}</Pill> : null}
              </div>
            </button>
          ))}
        </div>
      ) : null}

      {q.hasNextPage ? (
        <Button data-ai-more variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
          {t('adminAiPipeline.queue.more')}
        </Button>
      ) : null}
    </Card>
  );
}
